import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { menuService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { hasSizeOptions, isRegularSizeAvailable } from '../utils/sizeOptions';

const MenuPage = () => {
  const { isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Image popup states
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImage, setPopupImage] = useState({ url: '', name: '' });

  // Dynamic categories based on actual menu items
  const categories = React.useMemo(() => {
    const uniqueCategories = [...new Set(menuItems.map(item => item.category).filter(Boolean))];
    const categoryList = [{ id: 'all', name: 'All' }];
    
    uniqueCategories.forEach(category => {
      categoryList.push({
        id: category.toLowerCase().replace(/\s+/g, '-'),
        name: category.charAt(0).toUpperCase() + category.slice(1)
      });
    });
    
    return categoryList;
  }, [menuItems]);

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    const filterItems = () => {
      let filtered = [...menuItems];

      // Filter by category
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(item => {
          if (!item.category) return false;
          const categoryId = item.category.toLowerCase().replace(/\s+/g, '-');
          return categoryId === selectedCategory;
        });
      }

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.description && item.description.toLowerCase().includes(query)) ||
          (item.category && item.category.toLowerCase().includes(query))
        );
      }

      setFilteredItems(filtered);
    };

    filterItems();
  }, [menuItems, searchQuery, selectedCategory]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[MenuPage] Fetching menu items...');
      const response = await menuService.getAllItems();
      
      if (response.data && response.data.success && response.data.data) {
        const items = response.data.data;
        console.log('[MenuPage] Menu items fetched:', items.length);
        console.log('[MenuPage] Sample item categories:', items.slice(0, 5).map(item => ({ name: item.name, category: item.category })));
        setMenuItems(items);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('[MenuPage] Error fetching menu:', error);
      setError(error.response?.data?.message || 'Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    console.log('[MenuPage] Search query changed:', value);
    console.log('[MenuPage] Event type:', e.type);
    console.log('[MenuPage] Input is editable:', !e.target.readOnly);
    setSearchQuery(value);
  };

  const handleCategoryChange = (categoryId) => {
    console.log('[MenuPage] Category changed:', categoryId);
    setSelectedCategory(categoryId);
  };

  const handleRetry = () => {
    fetchMenuItems();
  };

  const handleImageClick = (imageUrl, itemName) => {
    if (imageUrl && imageUrl.trim() !== '') {
      setPopupImage({ url: imageUrl, name: itemName });
      setShowImagePopup(true);
    }
  };

  const closeImagePopup = () => {
    setShowImagePopup(false);
    setPopupImage({ url: '', name: '' });
  };

  if (loading) {
    return (
      <div className="menu-page">
        <div className="container">
          <div className="loading-container">
            <h2>Loading Menu...</h2>
            <div className="loading-spinner">🧋</div>
            <p>Preparing our delicious drinks for you!</p>
          </div>
        </div>
        
        <style jsx>{`
          .loading-container {
            text-align: center;
            padding: 100px 20px;
            min-height: 50vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          
          .loading-spinner {
            font-size: 3rem;
            animation: bounce 1s infinite;
            margin: 20px 0;
          }
          
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="menu-page">
        <div className="container">
          <div className="error-container">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button onClick={handleRetry} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
        
        <style jsx>{`
          .error-container {
            text-align: center;
            padding: 100px 20px;
            min-height: 50vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          
          .error-container h2 {
            color: #ef4444;
            margin-bottom: 15px;
          }
          
          .error-container p {
            color: #666;
            margin-bottom: 25px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="menu-page">
      <div className="container">
        <div className="menu-header">
          <h1>Our Menu</h1>
          <p className="menu-subtitle">
            Discover our carefully crafted drinks made with premium ingredients
          </p>
          
          {!isAuthenticated && (
            <div className="auth-notice">
              <p>
                <strong>Browse our menu below!</strong> To place an order, please{' '}
                <Link to="/auth" className="auth-link">sign up or login</Link> first.
              </p>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search drinks..."
              value={searchQuery}
              onChange={handleSearch}
              onInput={handleSearch}
              onClick={(e) => e.target.focus()}
              onFocus={() => console.log('[MenuPage] Search input focused')}
              className="search-input"
              autoComplete="off"
              readOnly={false}
              disabled={false}
              tabIndex={0}
              style={{ pointerEvents: 'auto', userSelect: 'text' }}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>

        {/* Category Filter */}
        <div className="category-section">
          <div className="category-container">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Results Info */}
        {!loading && !error && (
          <div className="results-info">
            <p>
              {searchQuery || selectedCategory !== 'all' ? (
                <>
                  Showing {filteredItems.length} of {menuItems.length} drinks
                  {searchQuery && <span> for "{searchQuery}"</span>}
                  {selectedCategory !== 'all' && (
                    <span> in {categories.find(cat => cat.id === selectedCategory)?.name}</span>
                  )}
                </>
              ) : (
                <>Showing all {menuItems.length} drinks</>
              )}
            </p>
          </div>
        )}

        {/* Menu Items */}
        <div className="menu-content">
          {filteredItems.length > 0 ? (
            <div className="menu-grid">
              {filteredItems.map(item => (
                <div key={item.id} className="menu-item">
                  <div className="item-image" onClick={() => handleImageClick(item.imageUrl, item.name)}>
                    {item.imageUrl && item.imageUrl.trim() !== '' ? (
                      <img 
                        src={item.imageUrl} 
                        alt={item.name}
                        className="menu-item-photo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span className="fallback-emoji" style={{display: (item.imageUrl && item.imageUrl.trim() !== '') ? 'none' : 'block'}}>
                      🚫📷
                    </span>
                  </div>
                  <div className="item-content">
                    <h3 className="item-name">{item.name}</h3>
                    <p className="item-description">{item.description}</p>
                    <div className="item-meta">
                      <span className="item-category">{item.category}</span>
                      <span className="item-price">
                        {hasSizeOptions(item) && isRegularSizeAvailable(item)
                          ? `from ${formatPrice(item.sizes?.regular || item.sizes?.small)}`
                          : formatPrice(item.price)
                        }
                      </span>
                    </div>
                    <Link 
                      to={`/drink/${item.id}`}
                      className="btn btn-primary btn-small"
                    >
                      {isAuthenticated ? 'Order Now' : 'View Details'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h3>No drinks found</h3>
              <p>
                {searchQuery || selectedCategory !== 'all' 
                  ? (
                    <>
                      Try adjusting your search or category filter.
                      <br />
                      <button 
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                        }}
                        className="btn btn-secondary btn-small"
                        style={{ marginTop: '15px' }}
                      >
                        Clear All Filters
                      </button>
                    </>
                  )
                  : 'Our menu is being updated. Please check back soon!'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Image Popup Modal */}
      {showImagePopup && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup" onClick={closeImagePopup}>✕</button>
            <img src={popupImage.url} alt={popupImage.name} className="popup-image" />
            <h3 className="popup-title">{popupImage.name}</h3>
          </div>
        </div>
      )}

      <style jsx>{`
        .menu-page {
          min-height: 100vh;
          padding: 20px 0 80px;
          background-color: #fff8f0;
        }
        
        .menu-header {
          text-align: center;
          padding: 40px 20px;
          background-color: #fcf0ff;
          border-radius: 15px;
          margin-bottom: 40px;
        }
        
        .menu-header h1 {
          font-size: 2.5rem;
          color: #C2185B;
          margin-bottom: 15px;
        }
        
        .menu-subtitle {
          font-size: 1.1rem;
          color: #666;
          margin-bottom: 20px;
        }
        
        .auth-notice {
          background-color: rgba(194, 24, 91, 0.1);
          border: 2px solid #C2185B;
          border-radius: 10px;
          padding: 15px 20px;
          margin-top: 20px;
        }
        
        .auth-notice p {
          margin: 0;
          color: #333;
        }
        
        .auth-link {
          color: #C2185B;
          font-weight: bold;
          text-decoration: none;
        }
        
        .auth-link:hover {
          text-decoration: underline;
        }
        
        .search-section {
          margin-bottom: 30px;
        }
        
        .search-container {
          position: relative;
          max-width: 500px;
          margin: 0 auto;
          z-index: 1;
        }
        
        .search-input {
          width: 100%;
          padding: 15px 50px 15px 20px;
          border: 2px solid #e5e5e5;
          border-radius: 30px;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.3s ease;
          pointer-events: auto !important;
          user-select: text !important;
          cursor: text !important;
          background-color: white !important;
          color: #333 !important;
        }
        
        .search-input:focus {
          border-color: #C2185B;
        }
        
        .search-icon {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 1.2rem;
          pointer-events: none;
          z-index: 2;
        }
        
        .category-section {
          margin-bottom: 40px;
        }
        
        .category-container {
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .category-btn {
          padding: 10px 20px;
          border: 2px solid #C2185B;
          background-color: transparent;
          color: #C2185B;
          border-radius: 25px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .category-btn:hover,
        .category-btn.active {
          background-color: #C2185B;
          color: white;
        }
        
        .results-info {
          text-align: center;
          margin: 20px 0;
          padding: 10px;
          background-color: rgba(194, 24, 91, 0.1);
          border-radius: 10px;
        }
        
        .results-info p {
          margin: 0;
          color: #666;
          font-size: 0.95rem;
        }
        
        .menu-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 25px;
        }
        
        .menu-item {
          background-color: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease;
        }
        
        .menu-item:hover {
          transform: translateY(-5px);
        }
        
        .item-image {
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 15px;
          border-radius: 10px;
          background-color: #f8f9fa;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          transition: transform 0.3s ease;
        }
        
        .item-image:hover {
          transform: scale(1.05);
        }
        
        .menu-item-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 10px;
        }
        
        .fallback-emoji {
          font-size: 3rem;
          color: #ccc;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          border: 2px dashed #e0e0e0;
          border-radius: 10px;
          background-color: #f9f9f9;
        }
        
        .fallback-emoji::after {
          content: "No Image Available";
          font-size: 0.8rem;
          color: #999;
          margin-top: 10px;
        }
        
        .item-name {
          font-size: 1.3rem;
          color: #C2185B;
          margin-bottom: 10px;
          font-weight: bold;
        }
        
        .item-description {
          color: #666;
          margin-bottom: 15px;
          line-height: 1.5;
        }
        
        .item-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .item-category {
          background-color: #f3f4f6;
          color: #666;
          padding: 4px 12px;
          border-radius: 15px;
          font-size: 0.85rem;
          text-transform: capitalize;
        }
        
        .item-price {
          font-size: 1.2rem;
          font-weight: bold;
          color: #C2185B;
        }
        
        .btn-small {
          padding: 8px 20px;
          font-size: 0.9rem;
          width: 100%;
          text-align: center;
        }
        
        .no-results {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }
        
        .no-results h3 {
          color: #C2185B;
          margin-bottom: 15px;
        }
        
        /* Image Popup Styles */
        .image-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .image-popup-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          background-color: white;
          border-radius: 15px;
          padding: 20px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }
        
        .close-popup {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #999;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.3s ease;
        }
        
        .close-popup:hover {
          background-color: #f0f0f0;
          color: #333;
        }
        
        .popup-image {
          max-width: 100%;
          max-height: 70vh;
          width: auto;
          height: auto;
          border-radius: 10px;
          object-fit: contain;
        }
        
        .popup-title {
          margin-top: 15px;
          color: #C2185B;
          font-size: 1.5rem;
          font-weight: bold;
        }
        
        @media (max-width: 768px) {
          .menu-header h1 {
            font-size: 2rem;
          }
          
          .category-container {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 10px;
          }
          
          .category-btn {
            white-space: nowrap;
          }
          
          .menu-grid {
            grid-template-columns: 1fr;
          }
          
          .image-popup-content {
            margin: 10px;
            padding: 15px;
          }
          
          .popup-image {
            max-height: 60vh;
          }
          
          .popup-title {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default MenuPage; 